<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import ContactNoteItem from 'dashboard/components-next/Contacts/ContactsSidebar/components/ContactNoteItem.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const props = defineProps({
  conversationId: {
    type: [String, Number],
    required: true,
  },
});

const store = useStore();
const visibleCount = ref(3);

const conversationMessages = computed(() => {
  const conversation = store.getters.getConversationById(props.conversationId);
  return conversation?.messages || [];
});

const isFreemium = computed(() => {
  const conversation = store.getters.getConversationById(props.conversationId);
  const accountId = store.getters.getCurrentAccountId;
  const hasModuleLumeraiTasks = store.getters[
    'accounts/isFeatureEnabledonAccount'
  ](accountId, FEATURE_FLAGS.LUMERAI_TASKS);
  const hasFreemium =
    store.getters['accounts/getAccount'](accountId)?.is_freemium_ai === true;
  return (
    (conversation?.is_freemium_ai === true || hasFreemium) &&
    !hasModuleLumeraiTasks
  );
});

const aiSummaries = computed(() => {
  return conversationMessages.value
    .filter(
      m =>
        m.content_attributes?.is_ai_summary === true ||
        (m.private && m.content && m.content.includes('Atualizado pela IA')) ||
        (m.private &&
          m.content &&
          m.content.includes('[RESUMO GERADO PELA LumerAI]'))
    )
    .reverse()
    .map(m => ({
      id: m.id,
      content: m.content,
      createdAt: m.created_at,
      user: { name: 'LumerAI (IA)' },
    }));
});

const visibleSummaries = computed(() => {
  return aiSummaries.value.slice(0, visibleCount.value);
});

const jumpToMessage = messageId => {
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
};

const showMore = () => {
  visibleCount.value += 5;
};

const isSummarizing = ref(false);

const hasModuleIa = computed(() => {
  const accountId = store.getters.getCurrentAccountId;
  return store.getters['accounts/isFeatureEnabledonAccount'](
    accountId,
    FEATURE_FLAGS.LUMERAI_TASKS
  );
});

const canGenerateSummary = computed(() => {
  if (aiSummaries.value.length === 0) return true;

  const latestSummaryCreatedAt = aiSummaries.value[0].createdAt;

  const hasNewMessages = conversationMessages.value.some(
    m =>
      m.created_at > latestSummaryCreatedAt &&
      !(
        m.content_attributes?.is_ai_summary === true ||
        (m.private &&
          m.content &&
          (m.content.includes('Atualizado pela IA') ||
            m.content.includes('[RESUMO GERADO PELA LumerAI]')))
      )
  );

  return hasNewMessages;
});

const generateSummary = async () => {
  if (isSummarizing.value) return;

  isSummarizing.value = true;
  const accountId = store.getters.getCurrentAccountId;

  try {
    const response = await window.axios.post(
      `/api/v1/accounts/${accountId}/lumerai/tasks/summarize`,
      { conversation_display_id: props.conversationId }
    );

    if (response.data && response.data.message) {
      // O backend já cria a nota interna automaticamente na API (tasks_controller.rb),
      // que será recebida via WebSocket, então não precisamos criar uma duplicada aqui.
      const { useAlert } = await import('dashboard/composables');
      useAlert('Resumo gerado e adicionado como nota interna com sucesso!');
    }
  } catch (error) {
    const { useAlert } = await import('dashboard/composables');
    useAlert(error.response?.data?.error || 'Erro ao gerar o resumo.');
  } finally {
    isSummarizing.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <div
      v-if="isFreemium"
      class="mx-6 mt-4 p-3 bg-indigo-50 border border-indigo-100 rounded-lg text-sm text-indigo-800"
    >
      <div class="flex items-center gap-2 mb-1 font-semibold">
        <i class="i-lucide-sparkles text-indigo-500" />
        {{ $t('AI_SUMMARIES.PRO_DEMO') }}
      </div>
      <p class="mb-0 text-xs text-indigo-700">
        {{ $t('AI_SUMMARIES.FREEMIUM_DESCRIPTION') }}
      </p>
    </div>

    <div class="px-6 py-4 flex flex-col gap-2">
      <Button
        v-if="hasModuleIa"
        variant="solid"
        color="blue"
        size="sm"
        icon="i-lucide-bot"
        :label="
          isSummarizing
            ? 'Gerando Resumo...'
            : !canGenerateSummary
              ? 'Resumo Atualizado'
              : 'Gerar Resumo da Conversa'
        "
        :disabled="isSummarizing || !canGenerateSummary"
        class="w-full justify-center shadow-sm hover:shadow"
        @click="generateSummary"
      />
    </div>

    <div v-if="aiSummaries.length > 0">
      <div
        v-for="summary in visibleSummaries"
        :key="summary.id"
        class="mx-6 py-4"
      >
        <ContactNoteItem
          :note="summary"
          written-by="LumerAI (IA)"
          collapsible
        />
        <div class="flex justify-end mt-2">
          <Button
            variant="link"
            color="blue"
            size="xs"
            icon="i-lucide-external-link"
            label="Ver Mensagem"
            @click="jumpToMessage(summary.id)"
          />
        </div>
      </div>
      <div
        v-if="aiSummaries.length > visibleCount"
        class="flex justify-center mt-2 pb-4"
      >
        <Button
          variant="faded"
          size="sm"
          :label="$t('AI_SUMMARIES.SHOW_MORE')"
          @click="showMore"
        />
      </div>
    </div>
    <p v-else class="px-6 py-6 text-sm leading-6 text-center text-n-slate-11">
      {{ $t('AI_SUMMARIES.EMPTY') }}
    </p>
  </div>
</template>
